import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from 'react-jss';
import { createMemoStyles, defaultBaseline, defaultFonts, uniqueId, safeRest } from '@wds/utils';
import { Button } from '@wds/button';
import { Input } from '@wds/input';
import { InputGroup, InputGroupAddon } from '@wds/input-group';
import { IconMinus, IconPlus } from '@wds/icons';

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}

function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}

var SIZES = /* #__PURE__ */Object.freeze(['lg', 'md', 'sm']);
/* #__PURE__ */

Object.freeze([].concat(_toConsumableArray(SIZES), ['xs']));
var NUMBER_PICKER_COLORS = /* #__PURE__ */Object.freeze(['success', 'warning', 'error']);

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

var useStyles = createMemoStyles(function (theme) {
  return {
    wrenchNumberPicker: function wrenchNumberPicker(_ref) {
      var stretched = _ref.stretched;
      return {
        display: stretched ? 'block' : 'inline-block',
        "& input[type='number']": {
          '-webkit-appearance': 'textfield'
        },
        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
          '-webkit-appearance': 'none',
          margin: 0
        }
      };
    },
    wrenchNumberPickerInput: function wrenchNumberPickerInput() {
      return {
        display: 'inline-flex',
        justifyContent: 'center',
        alignItems: 'center',
        minWidth: '3em',
        textAlign: 'center'
      };
    },
    wrenchNumberPickerAddonButton: function wrenchNumberPickerAddonButton() {
      return _objectSpread(_objectSpread(_objectSpread({}, defaultBaseline), defaultFonts(theme)), {}, {
        '& [class^="wrenchIcon"]': {
          marginRight: 0,
          marginLeft: 0
        }
      });
    }
  };
});
var _excluded = ["color", "label", "inputID", "max", "min", "onChange", "disabled", "step", "stretched", "value"];
/**
 * Adjusts the provided value to fit between (optional) min and max constraints, and ensures that it
 * is a proper whole number
 * @param {Number|String} val value to adjust
 * @param {Number} min min value (optional)
 * @param {Number} max max value (optional)
 * @returns Number
 */

var adjustValue = function adjustValue(val, min, max) {
  var initialValue = Number(val !== null && val !== void 0 ? val : '');
  if (val < min) initialValue = min;else if (max && val > max) initialValue = max;
  return Math.round(initialValue);
};

var NumberPicker = function NumberPicker(_ref) {
  var color = _ref.color,
      label = _ref.label,
      inputID = _ref.inputID,
      max = _ref.max,
      min = _ref.min,
      onChange = _ref.onChange,
      disabled = _ref.disabled,
      step = _ref.step,
      stretched = _ref.stretched,
      value = _ref.value,
      rest = _objectWithoutProperties(_ref, _excluded);

  var theme = useTheme();
  var classes = useStyles({
    stretched: stretched,
    theme: theme
  });
  var uniqueInputID = useMemo(function () {
    return inputID || uniqueId('input');
  }, [inputID]); /// //////////////// STATES ///////////////////

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      keyInputActive = _useState2[0],
      setKeyInputActive = _useState2[1];

  var _useState3 = useState(adjustValue(value, min, max)),
      _useState4 = _slicedToArray(_useState3, 2),
      stateValue = _useState4[0],
      setValue = _useState4[1];

  var shouldDisableMinus = stateValue <= min;
  var shouldDisablePlus = max && stateValue >= max; /// //////////////// EFFECTS ///////////////////

  useEffect(function () {
    setValue(adjustValue(value, min, max));
  }, [value, min, max]); /// //////////////// Handlers ///////////////////

  var handleChange = function handleChange(v) {
    if (onChange) {
      onChange(v);
    }
  };

  var handleMinus = function handleMinus() {
    var val = stateValue - step;
    var newValue = val < min ? min : val;
    handleChange(newValue);
    setValue(newValue);
  };

  var handlePlus = function handlePlus() {
    var val = stateValue + step;
    var newValue = max && val > max ? max : val;
    handleChange(newValue);
    setValue(newValue);
  };

  var handleInputChange = function handleInputChange(e) {
    var rawValue = Number(e.target.value); // we keep the input's raw value in our internal state

    setValue(rawValue);

    if (!keyInputActive) {
      handleChange(adjustValue(rawValue, min, max));
    }
  };

  var handleInputBlur = function handleInputBlur(e) {
    // if user has been typing, resume onChange handling
    if (keyInputActive) {
      setKeyInputActive(false);
      var adjusted = adjustValue(e.target.value, min, max);
      setValue(adjusted);
      handleChange(adjusted);
    }
  };

  var handleKeyDown = function handleKeyDown(e) {
    // if user starts typing numbers, pause onChange handling until blur
    if (!Number.isNaN(Number(e.key)) || e.key === 'Backspace') {
      setKeyInputActive(true);
    }
  };

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: classes.wrenchNumberPicker,
    onKeyDown: handleKeyDown
  }, /*#__PURE__*/React.createElement(InputGroup, {
    label: label,
    id: uniqueInputID,
    role: "group",
    stretched: stretched,
    color: color,
    disabled: disabled
  }, /*#__PURE__*/React.createElement(InputGroupAddon, {
    className: classes.wrenchNumberPickerAddonButton
  }, /*#__PURE__*/React.createElement(Button, {
    size: "sm",
    color: "muted",
    onClick: handleMinus,
    disabled: shouldDisableMinus || disabled,
    label: "Decrease value by ".concat(step),
    name: "decrement"
  }, /*#__PURE__*/React.createElement(IconMinus, {
    title: "Decrement Value"
  }))), /*#__PURE__*/React.createElement(Input, Object.assign({
    className: classes.wrenchNumberPickerInput,
    ariaLabel: label,
    id: uniqueInputID,
    inputSize: 3,
    value: Number(stateValue).toString(),
    onChange: handleInputChange,
    onBlur: handleInputBlur,
    type: "number",
    min: min,
    max: max,
    step: step,
    disabled: disabled
  }, safeRest(rest))), /*#__PURE__*/React.createElement(InputGroupAddon, {
    className: classes.wrenchNumberPickerAddonButton
  }, /*#__PURE__*/React.createElement(Button, {
    role: "button",
    size: "sm",
    color: "muted",
    onClick: handlePlus,
    disabled: shouldDisablePlus || disabled,
    label: "Increase value by ".concat(step)
  }, /*#__PURE__*/React.createElement(IconPlus, {
    title: "Increment Value"
  }))))));
};

NumberPicker.propTypes = {
  /**
   * Color variants
   */
  color: PropTypes.oneOf(NUMBER_PICKER_COLORS),

  /**
   * Label for input
   */
  label: PropTypes.node,

  /**
   * unique identifier
   */
  inputID: PropTypes.string,

  /**
   * Max value
   */
  max: PropTypes.number,

  /**
   * Min value
   */
  min: PropTypes.number,

  /**
   * onChange event handler
   * function(event: object) => void
   */
  onChange: PropTypes.func,

  /**
   * If true, input is disabled
   */
  disabled: PropTypes.bool,

  /**
   * Force the users input to increment at a fixed number
   */
  step: PropTypes.number,

  /**
   * Fill the entire width of parent container
   */
  stretched: PropTypes.bool,

  /**
   * Current input value
   */
  value: PropTypes.number
};
NumberPicker.defaultProps = {
  color: undefined,
  label: undefined,
  inputID: undefined,
  max: undefined,
  min: 0,
  onChange: undefined,
  disabled: false,
  step: 1,
  stretched: false,
  value: 0
};
export { NumberPicker };